import React, { useState } from 'react';
import { managerAPI } from '../config/api';
import APP_COLOURS from '../config/colors';
import Lottie from 'react-lottie';
import * as animationData from '../assets/lf30_editor_sdru9bf3.json';
import bg2 from '../assets/bg2.svg';
import tick from '../assets/tick_red.png';
import TraqLogo from './TraqLogo';
import ThemeToggle from './ThemeToggle';

const SignupPage = ({ onSignupSuccess, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    passcode: '',
    organisationID: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [emailValid, setEmailValid] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);

  React.useEffect(() => {
    const handleResize = () => {
      setScreenHeight(window.innerHeight);
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const validateEmail = (text) => {
    let reg = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (reg.test(text) === false) {
      setEmailValid(false);
      return false;
    } else {
      setEmailValid(true);
      return true;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'email') {
      validateEmail(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (error) setError('');
    if (success) setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsSubmitting(true);

    try {
      // Lowercase email for consistency
      const data = await managerAPI.signup(
        formData.first_name,
        formData.last_name,
        formData.email.toLowerCase(),
        formData.passcode,
        formData.organisationID
      );
      
      setSuccess(true);
      
      setTimeout(() => {
        if (onSignupSuccess) {
          onSignupSuccess(data);
        }
      }, 2000);
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || 'Signup failed. Please check your information and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  let contain =
    screenWidth * 0.9 > screenHeight * 0.9
      ? screenHeight * 0.89
      : screenWidth * 0.9;

  let maxContain = contain > 520 ? 520 : contain;
  let ar = 636 / 545;

  // Mobile Layout
  if (screenWidth < 700) {
    return (
      <div style={{
        backgroundColor: APP_COLOURS.BG,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '20px',
        paddingTop: '40px',
        position: 'relative',
        overflow: 'auto',
      }}>
        {/* Background gradient effect */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at top right, rgba(99, 102, 241, 0.15) 0%, transparent 50%)',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 60 }}>
          <ThemeToggle compact />
        </div>
        
        {/* Logo */}
        <div style={{ marginBottom: 20, zIndex: 10 }}>
          <TraqLogo height={48} />
        </div>

        {/* Signup Card */}
        <div style={{
          width: '100%',
          maxWidth: '340px',
          padding: 20,
          borderRadius: 20,
          backgroundColor: APP_COLOURS.BG2,
          boxShadow: `0 8px 32px ${APP_COLOURS.SHADOW}`,
          zIndex: 10,
          marginBottom: 20,
          border: `1px solid ${APP_COLOURS.CARD_BORDER}`,
        }}>
          {/* Logo at top of form */}
          <TraqLogo height={36} style={{ margin: '0 auto 8px' }} />

          {/* Success Message */}
          {success && (
            <div style={{
              width: '100%',
              marginTop: 10,
              padding: 10,
              backgroundColor: 'rgba(7, 213, 93, 0.1)',
              borderRadius: 8,
            }}>
              <p style={{
                color: APP_COLOURS.GREEN,
                fontSize: 12,
                textAlign: 'center',
                margin: 0,
              }}>
                Account created successfully! Redirecting to login...
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={{
              width: '100%',
              marginTop: 10,
              padding: 10,
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderRadius: 8,
            }}>
              <p style={{
                color: APP_COLOURS.RED,
                fontSize: 12,
                margin: 0,
              }}>
                {error}
              </p>
            </div>
          )}

          {/* Signup Form */}
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            {/* First Name & Last Name - Stack on mobile */}
            <div style={{ 
              display: 'flex', 
              flexDirection: screenWidth < 380 ? 'column' : 'row',
              gap: '10px', 
              marginTop: 12 
            }}>
              <div style={{
                flex: 1,
                padding: 10,
                backgroundColor: APP_COLOURS.BG,
                borderRadius: 10,
              }}>
                <input
                  type="text"
                  name="first_name"
                  placeholder="First name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  autoComplete="given-name"
                  required
                  disabled={isSubmitting || success}
                  style={{
                    width: '100%',
                    fontSize: 14,
                    color: APP_COLOURS.OFFWHITE,
                    fontWeight: '200',
                    fontFamily: 'Avenir, sans-serif',
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                  }}
                />
              </div>

              <div style={{
                flex: 1,
                padding: 10,
                backgroundColor: APP_COLOURS.BG,
                borderRadius: 10,
              }}>
                <input
                  type="text"
                  name="last_name"
                  placeholder="Last name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  autoComplete="family-name"
                  required
                  disabled={isSubmitting || success}
                  style={{
                    width: '100%',
                    fontSize: 14,
                    color: APP_COLOURS.OFFWHITE,
                    fontWeight: '200',
                    fontFamily: 'Avenir, sans-serif',
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                  }}
                />
              </div>
            </div>

            {/* Email Input */}
            <div style={{
              width: '100%',
              padding: 10,
              marginTop: 10,
              backgroundColor: APP_COLOURS.BG,
              borderRadius: 10,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              boxSizing: 'border-box',
            }}>
              {emailValid && (
                <img
                  src={tick}
                  alt="valid"
                  style={{
                    width: 16,
                    height: 16,
                    objectFit: 'contain',
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                />
              )}
              <input
                type="email"
                name="email"
                placeholder="Email address"
                value={formData.email}
                onChange={handleInputChange}
                autoComplete="email"
                required
                disabled={isSubmitting || success}
                style={{
                  width: '100%',
                  fontSize: 14,
                  color: APP_COLOURS.OFFWHITE,
                  fontWeight: '200',
                  fontFamily: 'Avenir, sans-serif',
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                }}
              />
            </div>

            {/* Password Input */}
            <div style={{
              width: '100%',
              padding: 10,
              marginTop: 10,
              backgroundColor: APP_COLOURS.BG,
              borderRadius: 10,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              boxSizing: 'border-box',
            }}>
              {formData.passcode.length > 0 && (
                <img
                  src={tick}
                  alt="valid"
                  style={{
                    width: 16,
                    height: 16,
                    objectFit: 'contain',
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                />
              )}
              <input
                type="password"
                name="passcode"
                placeholder="Passcode"
                value={formData.passcode}
                onChange={handleInputChange}
                autoComplete="new-password"
                required
                disabled={isSubmitting || success}
                style={{
                  width: '100%',
                  fontSize: 14,
                  color: APP_COLOURS.OFFWHITE,
                  fontWeight: '200',
                  fontFamily: 'Avenir, sans-serif',
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                }}
              />
            </div>

            {/* Organisation ID Input */}
            <div style={{
              width: '100%',
              padding: 10,
              marginTop: 10,
              backgroundColor: APP_COLOURS.BG,
              borderRadius: 10,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              boxSizing: 'border-box',
            }}>
              {formData.organisationID.length > 0 && (
                <img
                  src={tick}
                  alt="valid"
                  style={{
                    width: 16,
                    height: 16,
                    objectFit: 'contain',
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                />
              )}
              <input
                type="text"
                name="organisationID"
                placeholder="Organisation ID"
                value={formData.organisationID}
                onChange={handleInputChange}
                autoComplete="organization"
                required
                disabled={isSubmitting || success}
                style={{
                  width: '100%',
                  fontSize: 14,
                  color: APP_COLOURS.OFFWHITE,
                  fontWeight: '200',
                  fontFamily: 'Avenir, sans-serif',
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                }}
              />
            </div>

            {/* Submit Button */}
            <div style={{
              width: '100%',
              marginTop: 16,
            }}>
              {!isSubmitting ? (
                <button
                  type="submit"
                  disabled={isSubmitting || success}
                  style={{
                    backgroundColor: success ? APP_COLOURS.GREEN : APP_COLOURS.BLUE,
                    width: '100%',
                    padding: 12,
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 10,
                    border: 'none',
                    cursor: success ? 'default' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: success ? 0.8 : 1,
                  }}
                >
                  <span style={{ 
                    color: APP_COLOURS.WHITE,
                    fontSize: 14,
                    fontWeight: '600',
                  }}>
                    {success ? 'Account Created!' : 'Create Account'}
                  </span>
                </button>
              ) : (
                <div style={{
                  backgroundColor: APP_COLOURS.BLUE,
                  padding: 6,
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: 10,
                  width: 36,
                  height: 36,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  display: 'flex',
                }}>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                </div>
              )}
            </div>

            {/* Sign In Link */}
          <div style={{
              width: '100%',
              marginTop: 16,
              textAlign: 'center',
            }}>
              <p style={{
            color: APP_COLOURS.TEXTCOLOR,
                fontSize: 12,
                opacity: 0.7,
                margin: 0,
              }}>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={onSwitchToLogin}
                  style={{
                    color: APP_COLOURS.BLUE,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: 12,
                    fontWeight: '600',
                    padding: 0,
                  }}
                >
                  Sign in here
                </button>
              </p>
            </div>
          </form>
        </div>

        {/* Tagline */}
        <div style={{ 
            textAlign: 'center',
          zIndex: 10,
          marginBottom: 60,
        }}>
          <p style={{
            fontSize: 14,
            color: APP_COLOURS.TEXTCOLOR,
            fontFamily: 'Poppins, sans-serif',
            fontWeight: '400',
            margin: 0,
            opacity: 0.9,
          }}>
            Professional Staff & Retail management tools
          </p>
          </div>

        {/* Footer */}
        <div style={{
          position: 'fixed',
          bottom: 15,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          paddingRight: 20,
          paddingLeft: 20,
          zIndex: 10,
        }}>
          <p style={{
            fontSize: 14,
            color: APP_COLOURS.TEXTCOLOR,
            fontFamily: 'Poppins, sans-serif',
            fontWeight: '800',
            margin: 0,
          }}>
            tetrice
          </p>
          <p style={{
            fontSize: 12,
            color: APP_COLOURS.TEXTCOLOR,
            fontFamily: 'Poppins, sans-serif',
            fontWeight: '300',
            margin: 0,
            opacity: 0.7,
          }}>
            info@traq.app
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      width: screenWidth,
      height: screenHeight,
      backgroundColor: APP_COLOURS.BG,
      overflow: 'hidden',
      position: 'relative'
    }}>
      <div style={{ position: 'fixed', top: 20, right: 24, zIndex: 60 }}>
        <ThemeToggle />
      </div>
      {/* Background Pattern */}
      <div style={{
        width: screenWidth,
        height: screenHeight,
        right: 0,
        bottom: 0,
        position: 'absolute',
      }}>
        <img
          src={bg2}
          alt="background"
          style={{
            width: maxContain * ar,
            height: maxContain,
            objectFit: 'contain',
            position: 'absolute',
            left: -10,
            bottom: -10,
          }}
        />
      </div>

      {/* Lottie Animation */}
      <div style={{
        position: 'absolute',
        bottom: '10%',
        left: '1%',
      }}>
        <Lottie
          options={defaultOptions}
          height={contain > 600 ? 600 : contain}
          width={contain > 600 ? 600 : contain}
        />
      </div>

      {/* Main Content */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        width: screenWidth,
        height: screenHeight,
      }}>
        {/* Left Side - Logo */}
        <div style={{ flex: 1 }}>
          <div style={{
            position: 'absolute',
            top: '5%',
            left: '2%',
          }}>
            <TraqLogo height={48} />
          </div>
        </div>

        {/* Right Side - Signup Form */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-evenly',
          flexDirection: 'column',
        }}>
          <div style={{
            width: 350,
            height: 'auto',
            padding: 20,
            borderRadius: 25,
            backgroundColor: APP_COLOURS.BG2,
            boxShadow: `0 8px 32px ${APP_COLOURS.SHADOW}`,
            position: 'relative',
            zIndex: 10,
            border: `1px solid ${APP_COLOURS.CARD_BORDER}`,
          }}>
            {/* Logo at top of form */}
            <TraqLogo height={44} style={{ margin: '5px auto' }} />

            {/* Success Message */}
            {success && (
              <div style={{
                width: '100%',
                marginTop: 15,
                padding: 10,
                backgroundColor: 'rgba(7, 213, 93, 0.1)',
                borderRadius: 8,
              }}>
                <p style={{
                  color: APP_COLOURS.GREEN,
                  fontSize: 12,
                  textAlign: 'center',
                }}>
                  Account created successfully! Redirecting to login...
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div style={{
                width: '100%',
                marginTop: 15,
                padding: 10,
              }}>
                <p style={{
                  color: APP_COLOURS.RED,
                  fontSize: 12,
                }}>
                  {error}
                </p>
              </div>
            )}

            {/* Signup Form */}
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              {/* First Name & Last Name */}
              <div style={{ display: 'flex', gap: '10px', marginTop: 15 }}>
                <div style={{
                  flex: 1,
                  padding: 10,
                  borderWidth: 0.3,
                  borderColor: APP_COLOURS.BG,
                  backgroundColor: APP_COLOURS.BG,
                  borderRadius: 12,
                }}>
                  <input
                    type="text"
                    name="first_name"
                    placeholder="First name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    autoComplete="given-name"
                    required
                    disabled={isSubmitting || success}
                    style={{
                      width: '100%',
                      fontSize: 16,
                      textAlign: 'left',
                      color: APP_COLOURS.OFFWHITE,
                      fontWeight: '200',
                      fontFamily: 'Avenir, sans-serif',
                      letterSpacing: 0.26,
                      border: 'none',
                      outline: 'none',
                      backgroundColor: 'transparent',
                    }}
                  />
                </div>

                <div style={{
                  flex: 1,
                  padding: 10,
                  borderWidth: 0.3,
                  borderColor: APP_COLOURS.BG,
                  backgroundColor: APP_COLOURS.BG,
                  borderRadius: 12,
                }}>
                  <input
                    type="text"
                    name="last_name"
                    placeholder="Last name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    autoComplete="family-name"
                    required
                    disabled={isSubmitting || success}
                    style={{
                      width: '100%',
                      fontSize: 16,
                      textAlign: 'left',
                      color: APP_COLOURS.OFFWHITE,
                      fontWeight: '200',
                      fontFamily: 'Avenir, sans-serif',
                      letterSpacing: 0.26,
                      border: 'none',
                      outline: 'none',
                      backgroundColor: 'transparent',
                    }}
                  />
                </div>
              </div>

              {/* Email Input */}
              <div style={{
                width: '100%',
                padding: 10,
                marginTop: 15,
                borderWidth: 0.3,
                borderColor: APP_COLOURS.BG,
                backgroundColor: APP_COLOURS.BG,
                borderRadius: 12,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}>
                {emailValid && (
                  <img
                    src={tick}
                    alt="valid"
                    style={{
                      width: 20,
                      height: 20,
                      objectFit: 'contain',
                      marginRight: 10,
                    }}
                  />
                )}
                <input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  required
                  disabled={isSubmitting || success}
                  style={{
                    width: '100%',
                    fontSize: 16,
                    textAlign: 'left',
                    color: APP_COLOURS.OFFWHITE,
                    fontWeight: '200',
                    fontFamily: 'Avenir, sans-serif',
                    letterSpacing: 0.26,
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                  }}
                />
              </div>

              {/* Password Input */}
              <div style={{
                width: '100%',
                padding: 10,
                marginTop: 15,
                borderWidth: 0.3,
                borderColor: APP_COLOURS.BG,
                backgroundColor: APP_COLOURS.BG,
                borderRadius: 12,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}>
                {formData.passcode.length > 0 && (
                  <img
                    src={tick}
                    alt="valid"
                    style={{
                      width: 20,
                      height: 20,
                      objectFit: 'contain',
                      marginRight: 10,
                    }}
                  />
                )}
                <input
                  type="password"
                  name="passcode"
                  placeholder="Passcode"
                  value={formData.passcode}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  required
                  disabled={isSubmitting || success}
                  style={{
                    width: '100%',
                    fontSize: 16,
                    textAlign: 'left',
                    color: APP_COLOURS.OFFWHITE,
                    fontWeight: '200',
                    fontFamily: 'Avenir, sans-serif',
                    letterSpacing: 0.26,
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                  }}
                />
              </div>

              {/* Organisation ID Input */}
              <div style={{
                width: '100%',
                padding: 10,
                marginTop: 15,
                borderWidth: 0.3,
                borderColor: APP_COLOURS.BG,
                backgroundColor: APP_COLOURS.BG,
                borderRadius: 12,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}>
                {formData.organisationID.length > 0 && (
                  <img
                    src={tick}
                    alt="valid"
                    style={{
                      width: 20,
                      height: 20,
                      objectFit: 'contain',
                      marginRight: 10,
                    }}
                  />
                )}
                <input
                  type="text"
                  name="organisationID"
                  placeholder="Organisation ID"
                  value={formData.organisationID}
                  onChange={handleInputChange}
                  autoComplete="organization"
                  required
                  disabled={isSubmitting || success}
                  style={{
                    width: '100%',
                    fontSize: 16,
                    textAlign: 'left',
                    color: APP_COLOURS.OFFWHITE,
                    fontWeight: '200',
                    fontFamily: 'Avenir, sans-serif',
                    letterSpacing: 0.26,
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                  }}
                />
              </div>

              {/* Submit Button */}
              <div style={{
                width: '100%',
                marginTop: 15,
              }}>
                {!isSubmitting ? (
                  <button
                    type="submit"
                    disabled={isSubmitting || success}
                    style={{
                      backgroundColor: success ? APP_COLOURS.GREEN : APP_COLOURS.BLUE,
                      width: '100%',
                      padding: 10,
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: 15,
                      border: 'none',
                      cursor: success ? 'default' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: success ? 0.8 : 1,
                    }}
                    onMouseEnter={(e) => !success && (e.target.style.opacity = '0.9')}
                    onMouseLeave={(e) => !success && (e.target.style.opacity = '1')}
                  >
                    <span style={{ color: APP_COLOURS.WHITE }}>
                      {success ? 'Account Created!' : 'Create Account'}
                    </span>
                  </button>
                ) : (
                  <div style={{
                    backgroundColor: APP_COLOURS.BLUE,
                    padding: 6,
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 15,
                    width: 30,
                    height: 30,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    display: 'flex',
                  }}>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  </div>
                )}
              </div>

              {/* Sign In Link */}
              <div style={{
                width: '100%',
                marginTop: 15,
                textAlign: 'center',
              }}>
                <p style={{
                  color: APP_COLOURS.TEXTCOLOR,
                  fontSize: 12,
                  opacity: 0.7,
                }}>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={onSwitchToLogin}
                    style={{
                      color: APP_COLOURS.BLUE,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: 12,
                      fontWeight: '600',
                    }}
                  >
                    Sign in here
                  </button>
                </p>
              </div>
            </form>
          </div>

          {/* Bottom Text */}
          <div style={{ paddingLeft: 30 }}>
            <p style={{
              fontSize: 26,
              color: APP_COLOURS.TEXTCOLOR,
              fontFamily: 'Poppins, sans-serif',
              fontWeight: '400',
            }}>
              Professional Staff &
            </p>
            <p style={{
              fontSize: 26,
              color: APP_COLOURS.TEXTCOLOR,
              fontFamily: 'Poppins, sans-serif',
              fontWeight: '400',
            }}>
              Retail management tools
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        width: screenWidth,
        position: 'absolute',
        bottom: 30,
        left: 0,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingRight: 30,
        paddingLeft: 30,
      }}>
        <p style={{
          width: 110,
          fontSize: 16,
          color: APP_COLOURS.TEXTCOLOR,
          fontFamily: 'Poppins, sans-serif',
          fontWeight: '800',
        }}>
          tetrice
        </p>
        <p style={{
          width: 110,
          fontSize: 13,
          color: APP_COLOURS.TEXTCOLOR,
          fontFamily: 'Poppins, sans-serif',
          fontWeight: '300',
        }}>
          info@traq.app
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
